import notifiers from "../global/constants/NotificationConstants";
import strings from "../global/constants/StringConstants";
import urls from "../global/constants/UrlConstants";
import { logOutAction } from "../redux/authSlice";
import history from "./history";
import { store } from "./store";

const DEFAULT_TIMEOUT_MS = 30000;

export function getNoAuthCallParams(methodType: string, body?: any) {
  const params = {
    method: methodType,
    headers: strings.applicationJSON,
  };
  switch (methodType) {
    case "GET":
      return params;
    case "POST":
      return { ...params, body: JSON.stringify(body) };
  }
}

export async function getFileCallParams(body: any) {
  const accessToken = "Bearer " + store.getState().auth.accessToken;
  const auth = store.getState().auth;
  const orgId = auth.activeOrganizationId;
  // Do NOT set Content-Type — the browser must set it automatically with the
  // correct multipart boundary when body is FormData.
  const headers: any = { token: accessToken };
  if (orgId && orgId !== "all" && orgId !== "*") {
    headers["x-organization-id"] = String(orgId);
  }
  if (auth.userName) headers["x-user-name"] = String(auth.userName);
  if (auth.email) headers["x-user-email"] = String(auth.email);
  return {
    method: "POST",
    headers,
    body: body,
  };
}

export async function getHeaderObject(accessToken: string, contentType: any) {
  try {
    if (accessToken) {
      const auth = store.getState().auth;
      const orgId = auth.activeOrganizationId;
      const headers: any = {
        ...contentType,
        token: accessToken,
      };
      if (orgId && orgId !== "all" && orgId !== "*") {
        headers["x-organization-id"] = String(orgId);
      }
      if (auth.userName) {
        headers["x-user-name"] = String(auth.userName);
      }
      if (auth.email) {
        headers["x-user-email"] = String(auth.email);
      }
      return headers;
    }
    history.push(urls.landingViewPath);
    return null;
  } catch (error: any) {
    throw error;
  }
}

export const getCallParams = async (methodType: string, body?: any) => {
  const accessToken = store.getState().auth.accessToken;
  const headers = await getHeaderObject(
    accessToken,
    body instanceof FormData ? {} : strings.applicationJSON
  );
  const params: any = {
    method: methodType,
    headers,
  };

  switch (methodType) {
    case "GET":
      if (body) {
        const queryString = new URLSearchParams(body).toString();
        return { ...params, urlParams: queryString };
      }
      return params;
    case "DELETE":
      return {
        ...params,
        body: body instanceof FormData ? body : JSON.stringify(body),
      };
    case "POST":
      return {
        ...params,
        body: body instanceof FormData ? body : JSON.stringify(body),
      };
    case "PUT":
    case "PATCH":
      return {
        ...params,
        body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
      };
    default:
      return {
        ...params,
        body:
          body === undefined
            ? undefined
            : body instanceof FormData
            ? body
            : JSON.stringify(body),
      };
  }
};

async function parseErrorBody(response: Response) {
  const text = await response.text();
  if (!text) {
    return {
      message:
        response.status === 504
          ? "Server timed out (504). Please try again."
          : `Request failed (${response.status})`,
      status: response.status,
    };
  }
  try {
    const json = JSON.parse(text);
    return {
      ...json,
      message:
        typeof json?.message === "string"
          ? json.message
          : json?.message
            ? JSON.stringify(json.message)
            : response.status === 504
              ? "Server timed out (504). Please try again."
              : `Request failed (${response.status})`,
      status: response.status,
    };
  } catch {
    const cannotGet = text.match(/Cannot GET\s+[^\s<]+/i)?.[0];
    return {
      message:
        response.status === 504
          ? "Server timed out (504). Please try again."
          : cannotGet
            ? `API route not found (${response.status}): ${cannotGet.replace(/^Cannot GET\s+/i, "")}`
            : text.replace(/<[^>]+>/g, " ").trim().slice(0, 160) ||
              `Request failed (${response.status})`,
      status: response.status,
    };
  }
}

/** Always throw Error instances so CRA overlay / catch blocks get a readable message */
function toThrowError(error: any): Error {
  if (error instanceof Error) {
    if ((error as any).status == null && error && typeof error === "object") {
      // keep status if already set
    }
    return error;
  }
  let message = "Something went wrong";
  if (typeof error === "string") message = error;
  else if (typeof error?.message === "string") message = error.message;
  else if (error?.message != null) {
    try {
      message = JSON.stringify(error.message);
    } catch {
      message = String(error.message);
    }
  } else {
    try {
      message = JSON.stringify(error);
    } catch {
      message = String(error);
    }
  }
  const err = new Error(message);
  if (error && typeof error === "object") {
    Object.assign(err, { status: error.status, data: error });
  }
  return err;
}

export async function makeCall(
  callName: string,
  callParams: any,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    let url = callName;
    if (callParams?.urlParams) {
      url += `?${callParams.urlParams}`;
    }

    const response = await fetch(url, {
      ...callParams,
      signal: controller.signal,
    });

    if (!response.ok) {
      const body = await parseErrorBody(response);
      const err = toThrowError(body);
      // Soft-return instead of throw — prevents CRA "[object Object]" overlay
      console.error("[api]", err.message, callName);
      return {
        success: 0,
        message: err.message,
        data: null,
        status: (err as any).status || response.status,
        code: body?.code,
        existingMeetingId: body?.existingMeetingId,
      };
    }

    return await response.json();
  } catch (error: any) {
    if (error?.name === "AbortError") {
      const message = "Request timed out. Please try again.";
      console.error("[api]", message, callName);
      return { success: 0, message, data: null, status: 408 };
    }

    const errorStatus: any = await checkStatus(error);
    if (errorStatus?.status) {
      return {
        success: 0,
        message:
          typeof errorStatus.message === "string"
            ? errorStatus.message
            : "Session expired",
        data: null,
        status: 401,
      };
    }

    const err = toThrowError(
      error?.message
        ? error
        : { message: notifiers.GENERIC_ERROR, ...(error || {}) }
    );
    console.error("[api]", err.message, callName);
    return {
      success: 0,
      message: err.message,
      data: null,
      status: (err as any).status,
    };
  } finally {
    clearTimeout(timeout);
  }
}

export function getTimeoutPromise() {
  return new Promise((_resolve, reject) => {
    setTimeout(() => reject({ error: true, message: "Timeout" }), 15000);
  });
}

export const checkStatus = async (error: any) => {
  if (
    error?.status === 403 ||
    error?.status === 401 ||
    error?.message === "Failed to authenticate token."
  ) {
    store.dispatch(logOutAction());
    history.push(urls.landingViewPath);
    return { status: true, message: error.message };
  }
  return false;
};
